import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { auth } from '../services/api';
import { useTheme, useAppStyles } from '../theme';

export default function ForgotPasswordScreen() {
  const C = useTheme();
  const styles = useAppStyles();
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [newPass, setNewPass] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);

  const inp = (extra={}) => ({
    width:'100%', background:C.bg, border:'1px solid #2a2a4a',
    borderRadius:12, padding:'14px 16px', color:'#fff',
    fontSize:15, outline:'none', boxSizing:'border-box', marginBottom:14,
    ...extra
  });
  const focus = e => e.target.style.borderColor='#6366f1';
  const blur  = e => e.target.style.borderColor=C.inputBorder;

  // STEP 1 — send OTP after confirming email exists
  const sendOtp = async () => {
    if (!email.trim()) return toast.error('Enter your email address');
    setLoading(true);
    try {
      await auth.sendOtp(email.trim().toLowerCase());
      toast.success('Reset code sent! Check your email.');
      setStep(2);
    } catch(e) {
      const msg = (e.message||'').toLowerCase();
      if (msg.includes('not found')||msg.includes('no user')||msg.includes('404')||msg.includes('does not exist')) {
        toast.error('No account found with this email address');
      } else {
        toast.error(e.message||'Could not send reset code');
      }
    }
    setLoading(false);
  };

  // STEP 2 — verify OTP code
  const verifyOtp = async () => {
    if (otp.length < 4) return toast.error('Enter the reset code from your email');
    setLoading(true);
    try {
      await auth.verifyOtp(email.trim().toLowerCase(), otp.trim());
      toast.success('Code verified!');
      setStep(3);
    } catch(e) {
      toast.error('Wrong code. Check your email and try again.');
    }
    setLoading(false);
  };

  // STEP 3 — set new password
  const resetPassword = async () => {
    if (!newPass || newPass.length < 6) return toast.error('Password must be at least 6 characters');
    if (newPass !== confirm) return toast.error('Passwords do not match');
    setLoading(true);
    try {
      await auth.resetPassword({ email: email.trim().toLowerCase(), otp: otp.trim(), new_password: newPass });
      toast.success('Password reset successfully!');
      nav('/login');
    } catch(e) {
      toast.error(e.message||'Reset failed. Please try again.');
    }
    setLoading(false);
  };

  const strength = newPass.length===0?null:newPass.length<6?'weak':newPass.length<10?'good':'strong';
  const sColor = {weak:'#ef4444',good:'#f59e0b',strong:'#10b981'};

  return (
    <div style={{
      maxWidth:430, margin:'0 auto', 
      background:C.bg,
      display:'flex', alignItems:'center', justifyContent:'center', padding:24
    }}>
      <div style={{width:'100%', maxWidth:400}}>

        {/* Back */}
        <button onClick={()=>step===1?nav('/login'):setStep(s=>s-1)} style={{
          background:C.card, border:'1px solid #1e1e3a', color:C.muted,
          borderRadius:12, padding:'8px 16px', cursor:'pointer',
          fontSize:14, marginBottom:24
        }}>← Back</button>

        {/* Title */}
        <div style={{marginBottom:28}}>
          <div style={{fontSize:26,fontWeight:800,color:'#fff'}}>
            {step===1?'Forgot Password':step===2?'Enter Reset Code':'New Password'}
          </div>
          <div style={{fontSize:13,color:C.muted,marginTop:6}}>
            {step===1?'Enter your email to receive a reset code'
              :step===2?`We sent a 6-digit code to ${email}`
              :'Choose a new strong password'}
          </div>
        </div>

        {/* Card */}
        <div style={{
          background:C.card, border:'1px solid #1e1e3a',
          borderRadius:24, padding:'28px 24px',
          boxShadow:'0 24px 64px rgba(0,0,0,0.5)'
        }}>
          {/* Step progress */}
          <div style={{display:'flex',gap:6,marginBottom:24}}>
            {[1,2,3].map(i=>(
              <div key={i} style={{
                flex:1, height:3, borderRadius:3,
                background:step>=i?'#6366f1':C.border,
                transition:'background 0.3s'
              }}/>
            ))}
          </div>

          {/* STEP 1 — Email */}
          {step===1&&(
            <>
              <label style={{fontSize:11,color:C.muted,display:'block',marginBottom:8,fontWeight:600,letterSpacing:0.5}}>
                EMAIL ADDRESS
              </label>
              <input type="email" placeholder="your@email.com"
                value={email} onChange={e=>setEmail(e.target.value)}
                onKeyDown={e=>e.key==='Enter'&&sendOtp()}
                onFocus={focus} onBlur={blur}
                style={inp()} autoFocus
              />
              <button onClick={sendOtp} disabled={loading} style={{
                width:'100%',padding:'15px',
                background:loading?C.border:'linear-gradient(135deg,#6366f1,#8b5cf6)',
                border:'none',borderRadius:14,color:'#fff',
                fontSize:16,fontWeight:700,cursor:loading?'not-allowed':'pointer',
                boxShadow:loading?'none':'0 4px 20px #6366f133',marginBottom:16
              }}>{loading?'Checking...':'Send Reset Code →'}</button>
              <div style={{textAlign:'center',fontSize:13,color:C.muted}}>
                Remember it?{' '}
                <span onClick={()=>nav('/login')} style={{color:'#6366f1',cursor:'pointer',fontWeight:600}}>Sign in</span>
              </div>
            </>
          )}

          {/* STEP 2 — OTP */}
          {step===2&&(
            <>
              <div style={{
                background:'#6366f111',border:'1px solid #6366f133',
                borderRadius:12,padding:'12px 16px',marginBottom:20,textAlign:'center'
              }}>
                <div style={{fontSize:12,color:C.muted,marginBottom:2}}>Code sent to</div>
                <div style={{fontSize:14,fontWeight:700,color:'#fff'}}>{email}</div>
              </div>

              <label style={{fontSize:11,color:C.muted,display:'block',marginBottom:8,fontWeight:600,letterSpacing:0.5}}>
                6-DIGIT CODE
              </label>
              <input placeholder="• • • • • •"
                value={otp}
                onChange={e=>setOtp(e.target.value.replace(/\D/g,'').slice(0,6))}
                onKeyDown={e=>e.key==='Enter'&&verifyOtp()}
                onFocus={focus} onBlur={blur}
                style={inp({fontSize:30,fontWeight:800,letterSpacing:16,textAlign:'center'})}
                autoFocus
              />

              <button onClick={verifyOtp} disabled={loading} style={{
                width:'100%',padding:'15px',
                background:loading?C.border:'linear-gradient(135deg,#6366f1,#8b5cf6)',
                border:'none',borderRadius:14,color:'#fff',
                fontSize:16,fontWeight:700,cursor:loading?'not-allowed':'pointer',
                marginBottom:12
              }}>{loading?'Verifying...':'Verify Code →'}</button>

              <button onClick={sendOtp} disabled={loading} style={{
                width:'100%',padding:'11px',background:'none',
                border:'1px solid #1e1e3a',borderRadius:14,color:C.muted,
                fontSize:13,cursor:'pointer'
              }}>Resend Code</button>
            </>
          )}

          {/* STEP 3 — New Password */}
          {step===3&&(
            <>
              <label style={{fontSize:11,color:C.muted,display:'block',marginBottom:8,fontWeight:600,letterSpacing:0.5}}>
                NEW PASSWORD
              </label>
              <input type="password" placeholder="Min 6 characters"
                value={newPass} onChange={e=>setNewPass(e.target.value)}
                onFocus={focus} onBlur={blur}
                style={inp()} autoFocus
              />
              {strength&&(
                <div style={{marginTop:-10,marginBottom:14}}>
                  <div style={{height:3,borderRadius:3,background:sColor[strength],
                    width:strength==='weak'?'33%':strength==='good'?'66%':'100%',
                    transition:'all 0.3s'}}/>
                  <div style={{fontSize:11,color:sColor[strength],marginTop:4}}>
                    {strength==='strong'?'✓ Strong password':strength==='good'?'Good password':'Too short'}
                  </div>
                </div>
              )}

              <label style={{fontSize:11,color:C.muted,display:'block',marginBottom:8,fontWeight:600,letterSpacing:0.5}}>
                CONFIRM PASSWORD
              </label>
              <input type="password" placeholder="Repeat new password"
                value={confirm} onChange={e=>setConfirm(e.target.value)}
                onKeyDown={e=>e.key==='Enter'&&resetPassword()}
                onFocus={focus} onBlur={blur}
                style={inp()}
              />

              <button onClick={resetPassword} disabled={loading} style={{
                width:'100%',padding:'15px',
                background:loading?C.border:'linear-gradient(135deg,#10b981,#059669)',
                border:'none',borderRadius:14,color:'#fff',
                fontSize:16,fontWeight:700,cursor:loading?'not-allowed':'pointer',
                boxShadow:loading?'none':'0 4px 20px #10b98133'
              }}>{loading?'Resetting...':'✓ Reset Password'}</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
